import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  SelectMessagesParams,
  SelectMessagesQuery,
  SelectMessagesData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface SelectMessagesState
  extends MergeRequestState<SelectMessagesData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.selectMessages,
  isFetching: (state: ReduxState) => state.server.selectMessages.isFetching,
  data: (state: ReduxState) => state.server.selectMessages.data,
  error: (state: ReduxState) => state.server.selectMessages.error,
};

const initialState: SelectMessagesState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/selectMessages';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      query,
      config,
    }: {
      params: SelectMessagesParams;
      query: SelectMessagesQuery;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) => serverApi.selectMessages(params, query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerSelectMessages {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const selectMessages: ServerSelectMessages = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
